import React from 'react';
import { TaskyCard, TaskyCardHeader } from '../tasky';
import {
  BANNER_AB_STOPPED_REASON_LABELS,
  type BannerAbImageSummary,
  type BannerAbTestResult,
} from '../../types/bannerAbTest';

export interface FinalRecommendationCardProps {
  result: BannerAbTestResult;
  imagesById: Record<number, BannerAbImageSummary>;
}

/**
 * 自動改善ループの最終推奨。王者の系譜（初期案 → 最終案）と、なぜそこで止まったかを出す。
 * 予算切れ・派生案の全滅・改善なしはいずれも status=completed で返るので、
 * 停止理由はエラー表示ではなくこのカードで伝える（result.stopped_reason が正）。
 */
export const FinalRecommendationCard: React.FC<FinalRecommendationCardProps> = ({ result, imagesById }) => {
  const finalId = result.final_champion ?? result.winner_image_id;
  const finalImage = finalId == null ? undefined : imagesById[finalId];
  const lineage = result.lineage ?? [];

  return (
    <TaskyCard variant="plain" padding="md">
      <TaskyCardHeader>
        <h2 className="text-lg font-semibold text-dark">最終推奨バナー</h2>
      </TaskyCardHeader>

      {finalImage && (
        <img
          src={finalImage.url}
          alt="最終推奨バナー"
          className="mb-4 max-h-72 rounded-tasky-sm bg-warm-surface object-contain"
        />
      )}

      <dl className="mb-4 grid grid-cols-2 gap-4 sm:grid-cols-3">
        <div>
          <dt className="text-xs text-gray-500">回したラウンド</dt>
          <dd className="text-sm font-medium text-dark">{result.rounds ?? 0} ラウンド</dd>
        </div>
        <div>
          <dt className="text-xs text-gray-500">消費クレジット</dt>
          <dd className="text-sm font-medium text-dark">{result.total_credits}</dd>
        </div>
        <div>
          <dt className="text-xs text-gray-500">終了した理由</dt>
          <dd className="text-sm font-medium text-dark">
            {(result.stopped_reason && BANNER_AB_STOPPED_REASON_LABELS[result.stopped_reason]) || '-'}
          </dd>
        </div>
      </dl>

      {lineage.length > 0 && (
        <div>
          <div className="mb-1 text-xs font-semibold text-gray-500">選ばれるまでの経過</div>
          <div className="flex flex-wrap items-center gap-2">
            {lineage.map((id, index) => (
              <React.Fragment key={id}>
                {index > 0 && <span className="text-gray-400">→</span>}
                <div data-testid="lineage-item" className="text-center">
                  {imagesById[id] ? (
                    <img
                      src={imagesById[id].thumbnail_url || imagesById[id].url}
                      alt={`経過 #${id}`}
                      className="h-16 w-24 rounded border border-warm-border bg-warm-surface object-contain"
                    />
                  ) : (
                    <div className="h-16 w-24 rounded border border-warm-border bg-warm-surface" />
                  )}
                  <div className="mt-1 text-xs text-gray-500">#{id}</div>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      )}
    </TaskyCard>
  );
};

export default FinalRecommendationCard;
